import { computed, watch } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';
import { locationFacetValue, locationLabel } from '@/composables/records/recordsFormat';
import { BLANK_FACET_VALUE, type RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { useRecordsPage } from '@/composables/records/useRecordsPage';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys, useInsertions } from '@/plugins/store/actions/queries/insertions';
import { RECORD_ACTIONS_COLUMN_ID, type InsertionRow } from '@/types';
import { columnPresence, TOGGLE_COLUMN_IDS, type InsertionToggleColumnId } from './useInsertionsColumnVisibility';

const HEADINGS_KEY = 'views.pigs.records.table.headings';

/** The bulk insertion offered above the table: one insertion on a barn divided into its sections (the insertion form, in bulk). */
export const BULK_CREATE = {
    formId: 1,
    labelKey: 'views.pigs.insertions.table.createButton',
} as const;

/** i18n key for each toggleable column's heading, read off the same shared `records` namespace every
 * other records page's headings live under. */
const HEADING_KEYS: Record<InsertionToggleColumnId, string> = {
    supplier_chr: `${HEADINGS_KEY}.supplierChr`,
    sow_genetics: `${HEADINGS_KEY}.sowGenetics`,
    boar_genetics: `${HEADINGS_KEY}.boarGenetics`,
    gender: `${HEADINGS_KEY}.gender`,
    created_by: `${HEADINGS_KEY}.createdBy`,
};

/**
 * What the Insertions page filters on: both ends of the arrival, plus the four presence-driven facts
 * about the pigs themselves.
 *
 * The From columns only carry a chain when the arrival came from a transfer, so on a purchase every
 * one of them falls back to the blank option — which is a choice in the filter of its own, and is
 * how purchases are picked out, no column naming where the pigs came from.
 */
function facets(): RecordFacetDefinitions<InsertionRow> {
    const blank = i18n.global.t('views.pigs.records.table.texts.blank');

    return {
        to_barn: {
            value: (row) => locationFacetValue(row.to_farm, row.to_barn),
            label: (row) => locationLabel(row.to_farm, row.to_barn) || blank,
        },
        to_section: {
            value: (row) => row.to_section?.id ?? BLANK_FACET_VALUE,
            label: (row) => row.to_section?.name ?? blank,
        },
        from_barn: {
            value: (row) => locationFacetValue(row.from_farm, row.from_barn),
            label: (row) => locationLabel(row.from_farm, row.from_barn) || blank,
        },
        from_section: {
            value: (row) => row.from_section?.id ?? BLANK_FACET_VALUE,
            label: (row) => row.from_section?.name ?? blank,
        },
        supplier_chr: {
            value: (row) => row.supplier_chr ?? BLANK_FACET_VALUE,
            label: (row) => row.supplier_chr ?? blank,
        },
        sow_genetics: {
            value: (row) => row.sow_genetics ?? BLANK_FACET_VALUE,
            label: (row) => row.sow_genetics ?? blank,
        },
        boar_genetics: {
            value: (row) => row.boar_genetics ?? BLANK_FACET_VALUE,
            label: (row) => row.boar_genetics ?? blank,
        },
        gender: {
            value: (row) => row.gender ?? BLANK_FACET_VALUE,
            label: (row) => row.gender ?? blank,
        },
    };
}

/**
 * The columns, in the order an arrival reads: when it happened, where the pigs arrived, where they
 * came from, how many of them at what weight, then whichever of the presence-driven facts about the
 * pigs themselves the reader currently has on.
 *
 * Every filterable column here carries no accessor: it is unsorted, its cells render from the whole
 * row, and the values its header facet compares on belong to the filtering above. Give one an
 * accessor the day a column needs in-table sorting.
 */
function columns(isVisible: (columnId: InsertionToggleColumnId) => boolean): ColumnDef<InsertionRow>[] {
    const base: ColumnDef<InsertionRow>[] = [
        { id: 'date', accessorKey: 'date', header: i18n.global.t('views.pigs.records.table.headings.date'), enableSorting: true },
        { id: 'to_barn', header: i18n.global.t('views.pigs.records.table.headings.toLocation'), meta: { filterable: true } },
        { id: 'to_section', header: i18n.global.t('views.pigs.records.table.headings.toSection'), meta: { filterable: true } },
        { id: 'from_barn', header: i18n.global.t('views.pigs.records.table.headings.fromLocation'), meta: { filterable: true } },
        { id: 'from_section', header: i18n.global.t('views.pigs.records.table.headings.fromSection'), meta: { filterable: true } },
        { id: 'number', accessorKey: 'number', header: i18n.global.t('views.pigs.records.table.headings.number'), enableSorting: true, meta: { align: 'right' } },
        { id: 'total_weight', accessorKey: 'total_weight', header: i18n.global.t('views.pigs.records.table.headings.totalWeight'), enableSorting: true, meta: { align: 'right' } },
        { id: 'avg_weight', accessorKey: 'avg_weight', header: i18n.global.t('views.pigs.records.table.headings.avgWeight'), enableSorting: true, meta: { align: 'right' } },
    ];

    for (const columnId of TOGGLE_COLUMN_IDS) {
        if (columnId !== 'created_by' && isVisible(columnId)) {
            base.push({ id: columnId, header: i18n.global.t(HEADING_KEYS[columnId]), meta: { filterable: true } });
        }
    }

    // Sortable rather than filterable, and off until the reader turns it on.
    if (isVisible('created_by')) {
        base.push({ id: 'created_by', accessorKey: 'created_by', header: i18n.global.t(HEADING_KEYS.created_by), enableSorting: true });
    }

    return [...base, { id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } }];
}

/**
 * The Insertions page's configuration of the shared records page, plus the column-visibility control
 * over its four presence-driven facts about the pigs themselves, plus Created by. None of them are hard-gated - see
 * `useInsertionsColumnVisibility` - so availability never varies; only the reader's own stored choice,
 * else a presence-aware default (on once a loaded row actually carries it; Created by always defaults off), decides what's shown.
 */
export function useInsertionsPage() {
    const page = useRecordsPage<InsertionRow>({
        records: { use: useInsertions, rootKey: queryKeys.getInsertionsRoot },
        facets,
    });

    const presence = computed(() => columnPresence(page.rows.value));

    /**
     * Drops a facet's own active filter selection once its column is no longer on the table - a
     * hidden column has no chip left to clear it from, and `applyFacetFilters` would otherwise keep
     * matching rows against a filter the reader can no longer see or reach.
     */
    function clearFilterIfHidden(id: InsertionToggleColumnId) {
        if (id in page.columnFilters.value) {
            const next = { ...page.columnFilters.value };

            delete next[id];
            page.columnFilters.value = next;
        }
    }

    const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<InsertionToggleColumnId>({
        storageKey: 'pigs.insertions.columnVisibility',
        columnIds: TOGGLE_COLUMN_IDS,
        isAvailable: () => true,
        defaultVisible: (id) => id !== 'created_by' && presence.value[id],
        label: (id) => i18n.global.t(HEADING_KEYS[id]),
        onHide: clearFilterIfHidden,
    });

    // A period change (or any other change to the loaded set) can drop a column's only data, hiding
    // it via its own presence-based default even without a stored override - not just a user's own
    // toggle. Prune its filter the same way.
    watch(presence, () => {
        for (const id of TOGGLE_COLUMN_IDS) {
            if (!isVisible(id)) {
                clearFilterIfHidden(id);
            }
        }
    });

    // Computed rather than a constant so the headings follow a language switch.
    return { ...page, columns: computed(() => columns(isVisible)), columnToggleOptions, toggleColumn, setAllColumns };
}
